import { Link, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DSBox from '../../design-system/DSBox';
import { DSTypography } from '../../design-system/Typography';
import DSIconButton from '../../design-system/DSIconButton';
import DSSwitch from '../../design-system/DSSwitch';
import GiftView from './GiftView';
import { KLYDO_YELLOW } from '../../consts/colors';
import useGiftQuery from './hooks/useGiftQuery';
import { useHistory } from './hooks/useHistory';
import { GreetSplashScreen } from './GreetSplashScreen';
import GreetLogo from './GreetLogo';
import { useGiftContext } from './Context/GiftContextProvider';
import { useNavigate } from 'react-router-dom';
import { getEditGreetStep } from './utils';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import { isIOS, isMobile } from 'react-device-detect';
import DSSnackbar from '../../design-system/DSSnackbar';
// Match editor: iOS mobile 250px, desktop 290px → KlydoPlayer scales
const GIFT_SCALE_DESKTOP = 290 / 643;
const GIFT_SCALE_IOS = 250 / 643;

const GiftDone = () => {
  const theme = useTheme();
  const { search } = useHistory();
  const { setKlydoGiftProps, setSelectedKlydoGreet, giftCategories } =
    useGiftContext();
  const navigate = useNavigate();

  const { data: giftInfo, isLoading: isLoadingGift } = useGiftQuery({
    orderName: (search.orderName || search.orderId) as string,
    clockId: search.clockId as string,
  });

  const gift = giftInfo?.gift;
  const clockInfo = giftInfo?.clockInfo;
  const selectedKlydoGreet = gift?.klydo;
  const klydoGiftProps = {
    senderImageUrl: gift?.senderImageUrl,
    senderName: gift?.senderName,
    date: gift?.date,
    greetingContent: gift?.greetingContent,
    pendulumLogoUrl: gift?.pendulumLogoUrl,
    pendulumLogoType: gift?.pendulumLogoType,
    orderName: gift?.orderName || '',
    clockId: clockInfo?.id || '',
    klydoId: gift?.klydoId,
    timeZone: gift?.timeZone,
    giftId: gift?.giftId,
  };

  const [showGreeting, setShowGreeting] = useState(false);
  const [copiedSnackbarOpen, setCopiedSnackbarOpen] = useState(false);
  const isClockRegistered = clockInfo?.registerProduct;

  const handleEditGreet = () => {
    // The edit step and the name rule depend on the category list, so Edit
    // waits for it.
    if (isClockRegistered || !giftCategories) return;
    setKlydoGiftProps(klydoGiftProps);
    if (selectedKlydoGreet) setSelectedKlydoGreet(selectedKlydoGreet);
    const params = new URLSearchParams();
    const orderNameValue = search.orderName || search.orderId;
    if (orderNameValue) params.set('orderName', orderNameValue as string);
    if (search.clockId) params.set('clockId', search.clockId as string);
    params.set('step', getEditGreetStep(gift, giftCategories));
    navigate(`/gift/wizard?${params.toString()}`, { replace: true });
  };

  const getShareableLink = () => {
    const base = 'studio.klydoclock.com/gift/share/';
    if (klydoGiftProps?.orderName)
      return `${base}?orderName=${klydoGiftProps.orderName}`;
    if (klydoGiftProps?.clockId)
      return `${base}?orderName=${klydoGiftProps.clockId}`;
    return window.location.href;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareableLink());
    setCopiedSnackbarOpen(true);
  };

  const displayOrderInfo = () => {
    if (gift?.orderName === clockInfo?.id && clockInfo?.idf)
      return `Clock ID: ${clockInfo.idf}`;
    if (gift?.orderName) return `Order Name: ${gift.orderName}`;
    return '';
  };

  if (isLoadingGift) return <GreetSplashScreen />;

  return (
    <DSBox
      sx={{
        minHeight: '100vh',
        overflow: 'scroll',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <DSBox
        sx={{
          backgroundColor: theme.palette.background.paper,
          paddingY: 4,
          display: 'flex',
          justifyContent: 'center',
          textAlign: 'center',
          alignItems: 'center',
        }}
      >
        <GreetLogo color={KLYDO_YELLOW} width={isMobile ? 150 : 200} />
      </DSBox>

      <DSBox
        sx={{
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
          alignItems: 'center',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <DSTypography
          sx={{
            fontSize: 17,
            fontFamily: 'Gilroy-Medium',
            textAlign: isMobile ? 'center' : 'right',
            paddingRight: isMobile ? 0 : '20%',
          }}
        >
          {displayOrderInfo()}
        </DSTypography>

        <DSBox
          gap={isMobile ? 0.5 : 20}
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row-reverse',
            paddingTop: isMobile ? 0 : 8,
            justifyContent: 'center',
            textAlign: 'center',
            alignItems: 'center',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <DSBox sx={{ flexDirection: 'column', paddingTop: isMobile ? 0 : 2 }}>
            <DSBox>
              <DSTypography
                sx={{
                  lineHeight: 1,
                  fontStyle: 'bold',
                  fontSize: isMobile ? 35 : 47.52,
                  fontFamily: 'Gilroy-Bold',
                  color: '#FFFFFF',
                }}
              >
                Your KlydoGreet
              </DSTypography>
              <DSTypography
                sx={{
                  lineHeight: 1,
                  fontStyle: 'bold',
                  fontSize: isMobile ? 35 : 47.52,
                  fontFamily: 'Gilroy-Bold',
                  color: '#FFFFFF',
                }}
              >
                is ready!
                {!isClockRegistered && (
                  <DSIconButton
                    title="Edit greet"
                    onClick={handleEditGreet}
                    disabled={!giftCategories}
                    sx={{
                      '&:hover': { backgroundColor: 'transparent' },
                      '&:disabled': { color: '#A5A5A5' },
                    }}
                  >
                    <EditIcon />
                  </DSIconButton>
                )}
              </DSTypography>
            </DSBox>
            <DSBox py={1} />
            <DSBox sx={{ paddingBottom: { xs: 2 } }}>
              {[
                'Your personalized KlydoGreet has been',
                "set on the clock that's about to",
                'become a very special gift.',
                "We can't wait to hear",
                'all about their reaction!',
              ].map((line, i) => (
                <DSTypography
                  key={i}
                  sx={{ fontSize: 17, fontFamily: 'Gilroy-Medium' }}
                >
                  {line}
                </DSTypography>
              ))}
            </DSBox>

            {!isMobile && (
              <>
                <DSBox
                  onClick={handleCopyLink}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    paddingBottom: 2,
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 20, color: KLYDO_YELLOW }} />
                  <DSTypography
                    sx={{
                      color: KLYDO_YELLOW,
                      fontSize: 20,
                      fontFamily: 'Gilroy-Medium',
                    }}
                  >
                    share your greet with anyone
                  </DSTypography>
                </DSBox>
                <DSBox>
                  {[
                    'Have questions or something to share?',
                    "We'd love to hear from you!",
                  ].map((line, i) => (
                    <DSTypography
                      key={i}
                      sx={{ mb: 1, fontSize: 15, fontFamily: 'Gilroy-Regular' }}
                    >
                      {line}
                    </DSTypography>
                  ))}
                  <DSTypography
                    sx={{ mb: 1, fontSize: 15, fontFamily: 'Gilroy-Regular' }}
                  >
                    <Link
                      color="inherit"
                      href="https://www.klydoclock.com/pages/contact-us"
                    >
                      Contact Us
                    </Link>
                  </DSTypography>
                </DSBox>
              </>
            )}
          </DSBox>

          <DSBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GiftView
              gift={klydoGiftProps}
              klydo={selectedKlydoGreet!}
              isToggled={showGreeting}
              scale={isIOS && isMobile ? GIFT_SCALE_IOS : GIFT_SCALE_DESKTOP}
            />
            <DSBox
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                mt: 1,
              }}
            >
              <DSTypography
                sx={{
                  fontFamily: 'Gilroy-SemiBold',
                  fontSize: '16px',
                  color: showGreeting ? '#A5A5A5' : '#FFFFFF',
                }}
              >
                Greet
              </DSTypography>
              <DSSwitch
                checked={showGreeting}
                onChange={(e) => setShowGreeting(e.target.checked)}
              />
              <DSTypography
                sx={{
                  fontFamily: 'Gilroy-SemiBold',
                  fontSize: '16px',
                  color: showGreeting ? '#FFFFFF' : '#A5A5A5',
                }}
              >
                Message
              </DSTypography>
            </DSBox>
          </DSBox>
        </DSBox>

        {isMobile && (
          <>
            <DSBox
              onClick={handleCopyLink}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                paddingBottom: 2,
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 20, color: KLYDO_YELLOW }} />
              <DSTypography
                sx={{
                  color: KLYDO_YELLOW,
                  fontSize: 20,
                  fontFamily: 'Gilroy-Medium',
                }}
              >
                share your greet with anyone
              </DSTypography>
            </DSBox>
            <DSBox>
              {[
                'Have questions or something to share?',
                "We'd love to hear from you!",
              ].map((line, i) => (
                <DSTypography
                  key={i}
                  sx={{ mb: 1, fontSize: 15, fontFamily: 'Gilroy-Regular' }}
                >
                  {line}
                </DSTypography>
              ))}
              <DSTypography
                sx={{
                  mb: 1,
                  pb: 2,
                  fontSize: 15,
                  fontFamily: 'Gilroy-Regular',
                }}
              >
                <Link
                  color="inherit"
                  href="https://www.klydoclock.com/pages/contact-us"
                >
                  Contact Us
                </Link>
              </DSTypography>
            </DSBox>
          </>
        )}
      </DSBox>

      <DSSnackbar
        open={copiedSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopiedSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message="Link copied to clipboard!"
      />
    </DSBox>
  );
};

export default GiftDone;
